import { Router } from 'vue-router';
import type { RouteLocationNormalized } from 'vue-router';
import useSessionHanlder from '@/lib/useSessionHandler';
import useSessionValidate from '@/lib/useSessionValidate';
import { useLocalStorageStore } from '@/stores/localStorage';
import { useNoticePopupStore } from '@/stores/noticePopup';
import { useCenter } from '@/lib/_API/useCenter';
import { useGnbStore } from '@/stores/gnb';
import dayjs from '@/utils/dayjs';
import { checkAppVersionAndConfirmReload, installAppVersionCheckListeners } from '@/utils/appVersion';

/**
 * 같은 화면에서 query/hash만 바뀌는 replace·push에서는 GNB API를 다시 부르지 않음
 * (예: 수신·발신 조회 ?page= 동기화 시 beforeEach 중복 방지)
 */
function shouldReloadGnbMenu(to: RouteLocationNormalized, from: RouteLocationNormalized): boolean {
  if (from.matched.length === 0) return true;
  if (to.path !== from.path) return true;
  if (to.name !== from.name) return true;
  if (JSON.stringify(to.params) !== JSON.stringify(from.params)) return true;
  return false;
}

export default function routerBeforeAction(router: Router) {
  const { useSignout } = useSessionHanlder();
  const { useRouterSessionValidate } = useSessionValidate();
  let isInitialRoute = true;

  installAppVersionCheckListeners();

  router.beforeEach(async (to, from, next) => {
    const isTruthy = await useRouterSessionValidate();

    if (to.meta.requireAuth) {
      // 인증 필요한 페이지인 경우
      if (isTruthy) {
        // 라우트(경로·이름·params)가 바뀔 때만 GNB fetch — query만 변경 시 스킵
        if (shouldReloadGnbMenu(to, from)) {
          useGnbStore().loadGnbMenu();
        }
        next();

        //접수페이지 이동시
        if (to.fullPath.startsWith('/outbound')) {
          const outboundType = to.fullPath.slice(to.fullPath.lastIndexOf('/') + 1);

          const { getCenterNotice } = useCenter();

          //로컬스토리지에 오늘 접수공지팝업 표시 데이터없는 경우 저장
          const getPopupLocalStorage = localStorage.getItem('popupOpenFrom');
          if (getPopupLocalStorage) localStorage.setItem('popupOpenFrom', getPopupLocalStorage);
          else useLocalStorageStore().savePopupDataLocalStorage();

          //오늘 팝업 그만보기 확인
          const isOpenToday = (outboundType: string) => {
            const localIsShowToday = localStorage.getItem('popupOpenFrom');
            if (!localIsShowToday) return true;

            const { quick, parcel, registry, global } = JSON.parse(localIsShowToday);

            if (outboundType === 'quick') return dayjs().isSameOrAfter(quick, 'day');
            if (outboundType === 'parcel' || outboundType === 'mass-parcel')
              return dayjs().isSameOrAfter(parcel, 'day');
            if (outboundType === 'registry') return dayjs().isSameOrAfter(registry, 'day');
            if (outboundType === 'global') return dayjs().isSameOrAfter(global, 'day');
            else return true;
          };

          //url + isOpenToday 데이터계산
          const computedPopupOpen = (url: string, isTodayShow: boolean) => {
            if (url && isTodayShow) return true;
            else return false;
          };

          getCenterNotice().then((response) => {
            const noticePopupStore = useNoticePopupStore();
            noticePopupStore.updateNotice(response.centerResponse);

            switch (outboundType) {
              case 'parcel':
              case 'mass-parcel':
                noticePopupStore.updateIsNoticeOpen(
                  computedPopupOpen(noticePopupStore.notice.parcelImageUrl, isOpenToday(outboundType))
                );
                return;

              case 'quick':
                noticePopupStore.updateIsNoticeOpen(
                  computedPopupOpen(noticePopupStore.notice.quickImageUrl, isOpenToday(outboundType))
                );
                return;
              case 'registry':
                noticePopupStore.updateIsNoticeOpen(
                  computedPopupOpen(noticePopupStore.notice.mailImageUrl, isOpenToday(outboundType))
                );
                return;
              case 'global':
                noticePopupStore.updateIsNoticeOpen(
                  computedPopupOpen(noticePopupStore.notice.globalImageUrl, isOpenToday(outboundType))
                );
                return;
              default:
                noticePopupStore.updateIsNoticeOpen(false);
                return;
            }
          });
        }
      } else {
        useSignout();
        next({
          path: '/signin',
        });
        //세션스토리지에 저장
        sessionStorage.setItem('redirectURL', to.fullPath);
      }
    } else {
      // 인증 필요 없는 페이지 인 경우
      if (!isTruthy) useSignout();
      next();
    }
  });

  router.afterEach(() => {
    if (isInitialRoute) {
      isInitialRoute = false;
      return;
    }

    window.setTimeout(() => {
      checkAppVersionAndConfirmReload();
    }, 100);
  });
}
